/**
 * handlers/insights.ts
 * Load, display, and handle insight/notification interactions.
 */

import { state } from '../state';
import { dom } from '../dom';
import {
  markNotificationRead, markAllRead as apiMarkAllRead,
  getUnreadCount, getNotifications,
} from '../api';
import {
  appendMessage, renderInsightsList, updateUnreadBadge, showToast,
} from '../ui-renderers';
import { showChat } from './views';

export function fetchUnreadCount() {
  getUnreadCount()
    .then(data => {
      state.unreadCount = data.count || 0;
      updateUnreadBadge();
    })
    .catch(() => {});
}

export function loadInsights() {
  const list = dom.insList;
  if (!list) return;
  list.innerHTML = '<div class="ai-ins-loading">Loading insights&hellip;</div>';
  getNotifications(20)
    .then(data => renderInsightsList(data.notifications || []))
    .catch(() => {
      if (list) list.innerHTML = '<div class="ai-ins-empty">Could not load insights.</div>';
    });
}

export async function markAllInsightsRead() {
  try {
    await apiMarkAllRead();
    state.unreadCount = 0;
    updateUnreadBadge();
    loadInsights();
  } catch (_) {}
}

export function handleInsightClick({ id, link, title, message }: {
  id: string; link: string; title: string; message: string;
}) {
  markNotificationRead(id)
    .then(() => {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
      updateUnreadBadge();
    })
    .catch(() => {});

  const navigated = _navigateToInsightLink(link);
  if (!navigated) {
    _showInsightInChat(title, message);
  } else {
    showChat();
    if (typeof (window as any).AkiliBrain !== 'undefined' && (window as any).AkiliBrain.close) {
      (window as any).AkiliBrain.close();
    }
  }
}

function _navigateToInsightLink(link: string): boolean {
  if (!link) return false;
  if (link.startsWith('http')) { window.open(link, '_blank', 'noopener'); return true; }
  if (typeof (window as any).showSection !== 'function') return false;
  const clean   = link.replace(/^\//, '');
  const parts   = clean.split('/');
  const section = parts[0];
  const subPath = parts[1] || '';
  if (section === 'reports') { (window as any).showSection('reports'); return true; }
  if (section === 'matters' && subPath) {
    (window as any).showSection('matters');
    if (typeof (window as any).openMatterDetail === 'function') {
      setTimeout(() => (window as any).openMatterDetail(subPath), 150);
    }
    return true;
  }
  const KNOWN: Record<string, number> = {
    messages:1, dashboard:1, invoices:1, tasks:1, calendar:1,
    documents:1, clients:1, advocates:1, activity:1,
    billing:1, settings:1, reports:1,
  };
  if (KNOWN[section]) { (window as any).showSection(section); return true; }
  return false;
}

function _showInsightInChat(title: string, message: string) {
  showChat();
  setTimeout(() => {
    const text = (title ? '**' + title + '**\n\n' : '') + (message || '');
    appendMessage('bot', text.trim());
    const msgsEl = dom.msgs;
    if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
  }, 120);
}
